// <artwork-email-gate data-form-id data-product-title data-artist>
//
// Layers an email-capture step over the native Add to cart button. On the first
// add-to-cart attempt it blocks the action, asks for an email, subscribes the
// visitor as an email-marketing customer (tagged with the artist for routing),
// then lets the native add-to-cart proceed. Once captured, the gate stays open
// for the rest of the session so the visitor is not asked again.
//
// Degrades gracefully: if JS fails to load, the native Add to cart still works.

const SESSION_KEY = 'vangart:email-captured';

class ArtworkEmailGate extends HTMLElement {
  connectedCallback() {
    this.formId = this.getAttribute('data-form-id');
    this.productForm = document.getElementById(this.formId);
    if (!this.productForm) return;

    this.addButton = this.productForm.querySelector('button[name="add"]');
    this.dialog = this.querySelector('dialog');
    this.gateForm = this.querySelector('[data-gate-form]');
    this.emailInput = this.querySelector('input[type="email"]');
    this.errorEl = this.querySelector('[data-gate-error]');
    if (!this.addButton || !this.dialog || !this.gateForm) return;

    // Intercept the native add-to-cart in the capture phase, before the theme's
    // own handlers run, so we can hold it until the email is captured.
    this.onCaptureClick = (event) => {
      if (this.isUnlocked()) return;
      if (!this.addButton.contains(event.target) && event.target !== this.addButton) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.openDialog();
    };
    this.onCaptureSubmit = (event) => {
      if (this.isUnlocked()) return;
      if (event.target !== this.productForm) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.openDialog();
    };

    // --- GATE E-MAIL EN PAUSE (2026-07-03) ---
    // On autorise l'ajout au panier et la commande SANS demander l'e-mail à ce
    // niveau. L'interception native est désactivée : le bouton « Ajouter au
    // panier » fonctionne normalement et la modale ne s'ouvre plus.
    // Pour réactiver la capture d'e-mail, décommenter les deux lignes ci-dessous.
    // document.addEventListener('click', this.onCaptureClick, true);
    // document.addEventListener('submit', this.onCaptureSubmit, true);
    // ------------------------------------------

    this.gateForm.addEventListener('submit', (e) => this.handleGateSubmit(e));
    this.querySelectorAll('[data-gate-close]').forEach((btn) =>
      btn.addEventListener('click', () => this.closeDialog())
    );
    this.dialog.addEventListener('cancel', () => this.closeDialog());
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onCaptureClick, true);
    document.removeEventListener('submit', this.onCaptureSubmit, true);
  }

  isUnlocked() {
    try {
      return sessionStorage.getItem(SESSION_KEY) !== null;
    } catch (e) {
      return false;
    }
  }

  openDialog() {
    if (typeof this.dialog.showModal === 'function') {
      this.dialog.showModal();
    } else {
      this.dialog.setAttribute('open', '');
    }
    this.emailInput?.focus();
  }

  closeDialog() {
    if (typeof this.dialog.close === 'function') this.dialog.close();
    else this.dialog.removeAttribute('open');
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  async handleGateSubmit(event) {
    event.preventDefault();
    const email = (this.emailInput?.value || '').trim();
    if (!email || !this.emailInput.checkValidity()) {
      this.showError('Merci de saisir une adresse email valide.');
      this.emailInput?.focus();
      return;
    }

    const submitBtn = this.gateForm.querySelector('button[type="submit"]');
    if (submitBtn) submitBtn.disabled = true;

    await this.subscribe(email);

    // Mark captured for the session, then let the native add-to-cart through.
    try {
      sessionStorage.setItem(SESSION_KEY, email);
    } catch (e) {
      /* private mode — proceed anyway */
    }
    this.closeDialog();
    if (submitBtn) submitBtn.disabled = false;

    // Re-trigger the real add-to-cart (now unlocked).
    this.addButton.click();
  }

  // Subscribe as an email-marketing customer via the native customer form
  // endpoint. Tags carry the artist so the contact can be routed/segmented.
  async subscribe(email) {
    const artist = this.getAttribute('data-artist') || '';
    const tags = ['newsletter', 'artwork-buyer'];
    if (artist) tags.push('artiste:' + artist);

    const body = new FormData();
    body.append('form_type', 'customer');
    body.append('utf8', '✓');
    body.append('contact[email]', email);
    body.append('contact[tags]', tags.join(','));

    try {
      await fetch(window.location.pathname, {
        method: 'POST',
        body,
        headers: { Accept: 'text/html' },
        credentials: 'same-origin',
      });
    } catch (e) {
      // Never block the purchase on a subscription hiccup.
      console.warn('[artwork-email-gate] subscription failed', e);
    }
  }
}

if (!customElements.get('artwork-email-gate')) {
  customElements.define('artwork-email-gate', ArtworkEmailGate);
}
